<template>
  <div style="margin:20px">
    <FiPageRail title="Actuals - Driver Multiday Report"/>

    <div style="position: relative; font-weight: bold; font-size: 24px" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      <span v-if="driver_tags_options">
        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
        Tags:&nbsp;<b-form-select v-model="selected_driver_tag" :options="driver_tags_options" style="font-size: 18px; font-weight: bold; width:220px"/>
      </span>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      Driver:&nbsp;<b-form-select v-model="selected_driver" :options="driver_options" style="font-size: 18px; font-weight: bold; width:220px"/>
      <template v-if="selected_driver || selected_driver_tag">
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
        <b-button v-on:click.prevent="load_data(false)" :disabled="loading">Get Results</b-button>
        &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
        <b-button size="md" variant="success" :disabled="loading" @click="download">Download Report</b-button>
        <div v-if="summary.payroll_enabled && !selected_driver_tag" class="d-flex align-items-center justify-content-center">
          &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;<b-button size="md" variant="success" :disabled="loading" @click="push_to_payroll">Push to Payroll</b-button>
        </div>
      </template>
    </div>
    <div v-if="results_cached_at" class="text-center mt-1" style="font-size: 14px; color: #888;">
      Using data from {{ formattedCachedAt }}
      — <a href="#" @click.prevent="load_data(true)" style="font-size: 14px;">Pull New Data</a>
    </div>

    <div v-if="polling" class="mt-3 p-3 border rounded bg-light d-flex align-items-center justify-content-center">
      <b-spinner style="width: 3rem; height: 3rem;" class="mr-3" label="Loading..."></b-spinner>
      <span>
        <template v-if="!email_opt_in_sent">
          Report is still generating. Stay put, or <a href="#" @click.prevent="optInEmailWhenReady">get an in-app notification when it's done</a>.
        </template>
        <template v-else>
          Report is still generating. You'll get an in-app notification when it's done.
        </template>
      </span>
    </div>

    <div v-if="!loading && items_full.length > 0">
      <div class="row" style="margin:30px; font-weight: bold; font-size: 24px;">
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">{{items.length}} of {{summary.total_cnt}}</div>
        <div v-if="summary.total_cnt" class="col" style="border: 1px solid grey;">Time Diff&nbsp;&nbsp;<b-form-select v-model="duration_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div v-if="summary.total_cnt && summary.payroll_enabled" class="col" style="border: 1px solid grey;">Payroll Early Diff&nbsp;&nbsp;<b-form-select v-model="payroll_threshold" :options="duration_threshold_options" style="font-size: 20px; font-weight: bold; width:110px"/></div>
        <div class="col align-right d-flex align-items-center justify-content-center" style="border: 1px solid grey;">Filter&nbsp;<b-form-input style="width: 200px" v-model="booking_filter" placeholder="charterID,vehicle,driver"/></div>
      </div>
      <div class="row" style="margin:30px; font-weight: bold; font-size: 24px;">
        <div v-if="summary.hosHours" class="col" style="border: 1px solid grey;">Total HOS Hours: {{summary.hosHours}}</div>
        <div v-if="summary.hosPercentUtilized" class="col" style="border: 1px solid grey;">HOS Days Utilized: {{summary.hosPercentUtilized}}% </div>
        <div v-if="summary.payrollHours" class="col" style="border: 1px solid grey;">Total Payroll Hours: {{summary.payrollHours}}</div>
        <div v-if="summary.payrollPercentUtilized" class="col" style="border: 1px solid grey;">Payroll Days Utilized: {{summary.payrollPercentUtilized}}%</div>
      </div>
    </div>

    <table v-if="!loading && items.length > 0" width="100%" border="1" cellpadding="2">
      <thead>
        <tr>
          <th colspan="7" style="font-size: 18px; font-weight: bold;">&nbsp;</th>
          <th colspan="3" style="background-color: #C6C6C6">Planned</th>
          <th colspan="3" style="background-color: #D5D5D5">HOS</th>
          <th v-if="summary.payroll_enabled" colspan="3" style="background-color: #E5E5E5">Payroll</th>
          <th style="background-color: #F5F5F5">Time Diff</th>
          <th v-if="summary.payroll_enabled" style="background-color: #F5F5F5">Time Diff</th>
          <th>&nbsp;</th>
        </tr>
        <tr class="stripe">
          <th width="10px"></th>
          <th width="60px" @click="sort_strings('date')">Date</th>
          <th width="60px" @click="sort_strings('driver')">Driver</th>
          <th width="60px" @click="sort_strings('vehicle')">Vehicle</th>
          <th width="60px" @click="sort_numbers('charterID')">CharterID</th>
          <th width="60px" @click="sort_numbers('movementID')">MvmtID</th>
          <th width="60px" @click="sort_strings('is_multiday')">Multiday</th>
          <th width="60px" @click="sort_dates('plannedStartDateTime')">Start</th>
          <th width="60px" @click="sort_dates('plannedFinishDateTime')">Finish</th>
          <th width="60px" @click="sort_numbers('plannedMinutes')">Total</th>
          <th width="60px" @click="sort_dates('actualHOSStartDateTime')">Start</th>
          <th width="60px" @click="sort_dates('actualHOSFinishDateTime')">Finish</th>
          <th width="60px" @click="sort_numbers('hosMinutes')">Total</th>
          <th v-if="summary.payroll_enabled" width="60px" @click="sort_dates('actualPayrollStartDateTime')">Start</th>
          <th v-if="summary.payroll_enabled" width="60px" @click="sort_dates('actualPayrollFinishDateTime')">Finish</th>
          <th v-if="summary.payroll_enabled" width="60px" @click="sort_numbers('payrollMinutes')">Total</th>
          <th v-if="summary.payroll_enabled" width="60px" @click="sort_numbers('hosMinusPlanned')">Planned-Payroll</th>
          <th v-else width="60px" @click="sort_numbers('hosMinusPlanned')">HOS-Planned</th>
          <th v-if="summary.payroll_enabled" width="60px" @click="sort_numbers('hosMinusPayroll')">HOS-Payroll</th>
          <th width="200px" @click="sort_strings('notes')">Payroll Notes</th>
          <th width="200px">Notes</th>
        </tr>
      </thead>
      <template v-for="item in items">
        <tr class="stripe">
          <td><b-button size="sm" @click="toggle_div(item,'isDetailsVisible')">{{ item.isDetailsVisible ? '-' : '+' }}</b-button></td>
          <td>{{ item.date }}<a target="_blank" :href="item.driver_hos_link"><img style="margin: 0px 5px 5px 5px" src="/driver-icon.png"/></a></td>
          <td>{{ item.driver }}</td>
          <td>{{ item.vehicle }}</td>
          <td>{{ item.charterID }}</td>
          <td>{{ item.movementID }}</td>
          <td>{{ item.is_multiday }}</td>
          <td>{{item.plannedStartDateTime | formatDateTimeSmall}}</td>
          <td>{{item.plannedFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{item.plannedMinutes | formatMinutes}}</td>
          <td>{{item.actualHOSStartDateTime | formatDateTimeSmall}}</td>
          <td>{{item.actualHOSFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{item.hosMinutes | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled" :class="{ 'late-duration': isPayrollEarly(item) }">{{item.actualPayrollStartDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{item.actualPayrollFinishDateTime | formatDateTimeSmall}}</td>
          <td v-if="summary.payroll_enabled">{{item.payrollMinutes | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled">{{item.plannedMinusPayroll | formatMinutes}}</td>
          <td v-else>{{item.hosMinusPlanned  | formatMinutes}}</td>
          <td v-if="summary.payroll_enabled">{{item.hosMinusPayroll  | formatMinutes}}</td>
          <td align="left"><b-form-input style="border: 1px solid black; min-width:200px" v-model="item.event_notes" v-on:blur="save_notes(item)" @keydown.enter.native="save_notes(item)"/></td>
          <td>&nbsp;</td>
        </tr>
        <tr v-if="item.payroll_update" align="left" v-for="payroll_upd in item.payroll_update">
          <td colspan="2" style="color: green; font-weight: bold;padding: 15px;" align="right">
            PayNW
          </td>
          <td colspan="17" style="color: green; font-weight: bold;padding: 15px;" align="left">
              <span v-for="mapping in payroll_upd.mappings">
                <span v-if="mapping.includes('!')" style="color:darkred">{{mapping}}</span>
                <span v-else>{{mapping}}</span>
                &nbsp;&nbsp;|&nbsp;&nbsp;
              </span>
          </td>
        </tr>

        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.booking_results">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">planned data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID }}</router-link></td>
          <td>{{nested.is_multiday }}</td>
          <td>{{nested.plannedStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.plannedFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.plannedMinutes | formatMinutes}}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="item.isDetailsVisible" v-for="nested in item.hos_results">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID }}</router-link></td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>{{nested.actualHOSStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.actualHOSFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.hosMinutes}}</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
<!--        <tr v-if="nested.payroll_update" align="left" v-for="nested in item.hos_results">-->
<!--          <td colspan="2" style="color: green; font-weight: bold;padding: 15px;" align="right">-->
<!--            PayNW Update-->
<!--          </td>-->
<!--          <td colspan="17" style="color: green; font-weight: bold;padding: 15px;" align="left">-->
<!--              <span v-for="mapping in nested.payroll_update.mappings">-->
<!--                <span v-if="mapping.includes('!')" style="color:darkred">{{mapping}}</span>-->
<!--                <span v-else>{{mapping}}</span>-->
<!--                &nbsp;&nbsp;|&nbsp;&nbsp;-->
<!--              </span>-->
<!--          </td>-->
<!--        </tr>-->
        <tr style="border:2px solid var(--fi-rail-accent);" v-if="summary.payroll_enabled && item.isDetailsVisible" v-for="nested in item.payroll_results">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="font-weight: bold; color: white; text-align:right; background-color: gray">{{ nested.type }} data</td>
          <td>{{ nested.vehicle }}</td>
          <td>{{ nested.charterID }}</td>
          <td><router-link style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + nested.booking_hash }">{{ nested.movementID }}</router-link></td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>{{nested.actualPayrollStartDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.actualPayrollFinishDateTime | formatDateTimeSmall}}</td>
          <td>{{nested.payrollMinutes | formatMinutes}}</td>
          <td>&nbsp;</td>
          <td v-if="summary.payroll_enabled">&nbsp;</td>
          <td>{{ nested.notes }}</td>
          <td align="left">{{ formatPunchExceptions(nested.punch_exceptions) }}</td>
        </tr>
      </template>
      <tr style="background-color: var(--fi-rail-accent);">
        <td colspan="12">&nbsp;</td>
        <td>{{summary.hosMinutes}}m</td>
        <td v-if="summary.payroll_enabled">&nbsp;</td>
        <td v-if="summary.payroll_enabled">&nbsp;</td>
        <td v-if="summary.payroll_enabled">{{summary.payrollMinutes}}m</td>
        <td>&nbsp;</td>
        <td v-if="summary.hosMinusPayrollMinutes">{{summary.hosMinusPayrollMinutes}}m</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
      </tr>
    </table>
    <div v-if="loading && !polling">
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
  </div>

</template>
<script>
import {api_url, get_actuals_driver_multiday_report, get_driver_list, get_tag_list, push_actuals_driver_multiday_to_payroll, register_email_when_ready, save_event_notes} from '../networking/apis'
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from "axios";

export default {
  name: 'actuals_driver_multiday_report',

  components: {
  },

  data() {
    return {
      booking_filter: '',
      items: [],
      items_full: [],
      summary: [],
      driver_tags_options: [],
      selected_driver: null,
      selected_driver_tag: null,
      driver_options: [],
      duration_threshold: null,
      payroll_threshold: null,
      early_threshold: 10,
      late_threshold: 10,
      duration_threshold_options: [
        { value: null, text: 'All' },
        { value: '5', text: '> 5m' },
        { value: '10', text: '> 10m' },
        { value: '15', text: '> 15m' },
        { value: '30', text: '> 30m' },
        { value: '45', text: '> 45m' },
        { value: '60', text: '> 1h' },
        { value: '120', text: '> 2h' },
      ],
      loading: false,
      results_cached_at: null,
      polling: false,
      email_opt_in_sent: false,
      details_fromDate: justDate(moment().subtract(7, 'days')),
      details_toDate: justDate(moment().subtract(1, 'days')),

      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(90, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },
  created() {
    this.load_drivers()
  },
  methods: {
    // FI-1432: user opts in to receive an email when this long-running multiday report finishes.
    async optInEmailWhenReady() {
      try {
        await register_email_when_ready({
          tenant: this.user.tenant,
          report_type: 'driver_multiday_report',
          params: {
            driver_tag: this.selected_driver_tag,
            driver: this.selected_driver,
            from_date: this.details_fromDate,
            to_date: this.details_toDate,
          },
        })
        this.email_opt_in_sent = true
      } catch (e) {
        this.$emit('showAlert', "couldn't save the notification preference — try again", 'danger', 4000)
      }
    },
    async load_drivers() {
      this.driver_options = []
      this.driver_options_full = []
      this.driver_tags_options = []
      try {
        const {value: {data = []}} = await get_driver_list(this.user.tenant)
        if (data.length > 0 && data[0].value === '') data[0].text = 'Select driver...'
        this.driver_options = data
        this.driver_options_full = data
      } catch(e) {
        this.$emit('showAlert', 'error loading data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.driver_tags_options = this.getUniqueSortedTags(this.driver_options_full)
    },
    getUniqueSortedTags(arr) {
      const allTags = arr.flatMap(item => {
        if (!item || !item.tags) return [];

        return item.tags.split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);
      });

      const uniqueTags = [...new Set(allTags)];
      uniqueTags.sort();
      return uniqueTags;
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    async load_data(reprocess) {
      const days = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'),'days')
      if (this.selected_driver_tag && days > 7) {
        alert("please limit tag based date range to 1 week or less")
        return;
      }
      else if (this.selected_driver && days > 14) {
        alert("please limit tag based date range to 2 weeks or less")
        return;
      }

      if (this._retryTimerId) { clearTimeout(this._retryTimerId); this._retryTimerId = null }
      if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
      this.loading = true
      this.results_cached_at = null
      if (!this.polling) {
        this._pollingTimerId = setTimeout(() => { this._pollingTimerId = null; this.polling = true }, 30000)
      }
      try {
        const {value: {data = []}} = await get_actuals_driver_multiday_report(this.user.tenant, this.selected_driver_tag, this.selected_driver, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), reprocess, this.polling ? { timeout: 30000, poll: true } : {})
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        if (data.status === 'processing') {
          this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 10000)
          return
        }
        this.polling = false
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.results
        this.items_full = data.results
        this.results_cached_at = data.results_cached_at || null
        this.loading = false
        this.filter_items()
      } catch (e) {
        if (this._pollingTimerId) { clearTimeout(this._pollingTimerId); this._pollingTimerId = null }
        this.polling = true
        this.loading = false
        this.summary = []
        this.items = []
        this.items_full = []
        this._retryTimerId = setTimeout(() => { this._retryTimerId = null; this.load_data(false) }, 30000)
      }
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    async save_notes(item) {
      try {
        let json = {
          'event_hash': item.event_notes_hash,
          'event_target': item.event_target,
          'notes': item.event_notes
        }
        const {value: { data = [] } } = await save_event_notes(this.user.tenant, json)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    async push_to_payroll() {
      try {
        this.$emit('showAlert', 'processing push to payroll request', 'info', 5000)
        const {value: {data = []}} = await push_actuals_driver_multiday_to_payroll(this.user.tenant, this.selected_driver_tag, this.selected_driver, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.$emit('showAlert', 'pushed data to payroll successfully', 'success', 5000)
      } catch(e) {
        this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
      }
    },
    filter_items() {
      this.items = this.items_full
      if (this.selected_type)
        this.items = this.items.filter(item => item.booking_type == this.selected_type)
      if (this.booking_filter)
        this.items = this.items.filter(item => (this.contains(item.charterID,this.booking_filter) || this.contains(item.event_notes,this.booking_filter) || this.contains(item.driver,this.booking_filter) || this.contains(item.vehicle,this.booking_filter)))
      if (this.duration_threshold)
        this.items = this.items.filter(item => (Math.abs(item.plannedMinusPayroll) > this.duration_threshold) || (Math.abs(item.hosMinusPlanned) > this.duration_threshold) || (Math.abs(item.hosMinusPayroll) > this.duration_threshold))
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },
    sort_dates(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {

          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return new Date(a[col]) - new Date(b[col])
          }
        })
      }
      this.sort_col = col
    },
    sort_numbers(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            return a[col] - b[col]
          }
        })
      }
      this.sort_col = col
    },
    sort_strings(col) {
      if (this.sort_col == col) {
        this.items = this.items.reverse()
      } else {
        this.items = this.items.sort((a, b) => {
          if (a[col] && !b[col]) {
            return -1
          } else if (!a[col] && b[col]) {
            return 1
          } else if (!a[col] && !b[col]) {
            return -1
          } else {
            let fa = a[col].charAt(0).toLowerCase(),
                fb = b[col].charAt(0).toLowerCase();

            if (fa < fb) {
              return -1;
            }
            if (fa > fb) {
              return 1;
            }
            return 0;
          }
        })
      }
      this.sort_col = col
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info')
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/reports/actuals/driver/multiday/download`,
          {
            tenant: this.user.tenant,
            tag: this.selected_driver_tag,
            driver: this.selected_driver,
            fromDate: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            toDate: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `payroll-driver-${this.details_fromDate}-${this.details_toDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'data could not be downloaded.', 'danger')
      })
    },
    isPayrollEarly(item) {
      if (!this.payroll_threshold || !item.actualPayrollStartDateTime || !item.plannedStartDateTime) return false;
      const diff = moment(item.plannedStartDateTime).diff(moment(item.actualPayrollStartDateTime), 'minutes');
      return diff > this.payroll_threshold;
    },
    formatPunchExceptions(punch_exceptions) {
      if (!punch_exceptions || !Array.isArray(punch_exceptions) || punch_exceptions.length === 0) return '';
      return punch_exceptions.join(' | ');
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user
    }),
    formattedCachedAt() {
      if (!this.results_cached_at) return ''
      const m = moment(this.results_cached_at)
      return m.isValid() ? m.format('MMM D, YYYY h:mm A') : this.results_cached_at
    }
  },
  beforeDestroy() {
    if (this._retryTimerId) clearTimeout(this._retryTimerId)
    if (this._pollingTimerId) clearTimeout(this._pollingTimerId)
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.items = []
        this.items_full = []
        this.summary = null
        this.load_drivers()
      }
    },
    duration_threshold(val) {
      this.filter_items()
    },
    booking_filter() {                            
      this.filter_items()
    },
    selected_driver_tag(val) {
      if (val) {
        this.selected_driver = null
        this.driver_options = this.driver_options_full.filter(item => item.tags && item.tags.indexOf(this.selected_driver_tag) !== -1)
      }
    },
    selected_driver(val) {
      if (val)
        this.selected_driver_tag = null
    },
  }
}
</script>

<style scoped>

thead {
  position: sticky;
  top: 0;
  background-color: #F9F8F8;
  border: 1px solid black;
}

tr:nth-child(even) {
  background-color: #f2f2f2 !important;
}

.arrows {font-size: 22px; font-weight: bold; color: var(--fi-rail-accent) !important }


</style>
